import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { useApi } from "./useApi";
import { apiErrorMessage } from "../utils/apiError";
import { useLatest } from "./useLatest";
import {
  EVIDENCE_IN_PROGRESS_STATUSES,
  EVIDENCE_IN_FLIGHT_TIMEOUT_MS,
  EVIDENCE_TRACKING_FOLLOW_UP_MS,
  EVIDENCE_STALLED_AFTER_MS,
  EvidenceConclusion,
  EvidenceDraftSummary,
  EvidenceOrderType,
  CustomerContact,
  DigitalDetails,
  EvidenceOutline,
  EvidenceSubmissionView,
  ExcludableSection,
  ForceableSection,
  FactId,
  ManualRefund,
  PackageFileKey,
  ShopifyAnswerField,
  StripeAnswerField,
  SUBMISSION_PROVIDERS,
  type SubmissionProvider,
} from "../types/evidence";
import { BankCase, BankCaseOrderBody, BankFactsBody, EvidenceOrderBody } from "../types/evidenceBankCase";

export const POLL_INTERVAL_MS = 2000;
const OPENED_AFTER_HIDDEN_MS = 10 * 60 * 1000;
const SUBMIT_COPY: Record<SubmissionProvider, { done: string; refused: string }> = {
  shopify: { done: "Saved to Shopify.", refused: "Shopify refused the submission." },
  paypal: { done: "Submitted to PayPal.", refused: "PayPal refused the submission." },
  adyen: { done: "Defended in Adyen.", refused: "Adyen refused the defence." },
  stripe: { done: "Submitted to Stripe.", refused: "Stripe refused the submission." },
};

export interface EvidenceDispute {
  dispute_id: number;
  status: string;
  pp_type: string;
  pp_amount: number;
  pp_currency: string;
  pp_reason: string;
  pp_status: string | null;
  pp_evidence_due_by: string | null;
  can_submit_evidence: boolean | null;
  paypal_buyer_email: string | null;
}

/** What the page is opened on: `id` comes off the route, so it is a string and may be missing. */
export type EvidenceOwnerRef = { kind: "dispute" | "bank_case"; id: string | undefined };

export function useEvidenceDraft({ kind, id }: EvidenceOwnerRef) {
  // The two owners answer the same three requests under their own path.
  const ownerPath = kind === "dispute" ? `/api/disputes/${id}` : `/api/evidence-bank-cases/${id}`;
  const ownerKey = `${kind}:${id}`;
  // Separate instances: only the reads drive the page spinner, so a mutation can't blank the page.
  const { get, loading } = useApi();
  const { getText } = useApi();
  const { post } = useApi();
  const { patch } = useApi();
  const { put } = useApi();
  const { download } = useApi();

  const [dispute, setDispute] = useState<EvidenceDispute | null>(null);
  const [bankCase, setBankCase] = useState<BankCase | null>(null);
  const [draft, setDraft] = useState<EvidenceDraftSummary | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewStale, setPreviewStale] = useState(false);
  const [previewExpired, setPreviewExpired] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [outline, setOutline] = useState<EvidenceOutline | null>(null);
  const [outlineError, setOutlineError] = useState(false);
  const [disputeError, setDisputeError] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  // Generate supersedes whatever draft the server holds, so it stays closed until this settles.
  const [draftLoaded, setDraftLoaded] = useState(false);
  const loadError = disputeError ?? draftError;
  // A failed draft GET also leaves `draft` null, and offering Generate then supersedes the real
  // draft server-side, so the page needs the two apart.
  const draftLoadFailed = draftError !== null;
  const [orderTypeRequired, setOrderTypeRequired] = useState(false);
  const [orderPickRequired, setOrderPickRequired] = useState(false);
  const [pendingAction, setPendingAction] = useState<"generate" | "conclusion" | "stopWaiting" | "submit" | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: "success" | "error" | "info" } | null>(null);
  const [submission, setSubmission] = useState<EvidenceSubmissionView | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // isCurrent guards each fetch: navigating changes the path, so useApi's same-path supersede
  // no longer drops the previous dispute's slower response.
  const fetchOwner = useCallback(async (isCurrent: () => boolean = () => true) => {
    try {
      const res = await get<{ dispute?: EvidenceDispute; bankCase?: BankCase }>(ownerPath);
      if (!isCurrent()) return;
      setDispute(res.dispute ?? null);
      setBankCase(res.bankCase ?? null);
      setDisputeError(null);
    } catch (err: any) {
      Sentry.captureException(err);
      if (!isCurrent()) return;
      setDisputeError(apiErrorMessage(err, kind === "dispute" ? "Failed to load the dispute" : "Failed to load the case"));
    }
  }, [get, ownerPath, kind]);

  const openedDraftId = useRef<number | null>(null);
  const hiddenSince = useRef<number | null>(null);
  // Fire and forget: the row only anchors the admin trail, nothing on the page depends on it.
  const reportOpened = useCallback((draftId: number) => {
    openedDraftId.current = draftId;
    post(`/api/evidence-drafts/${draftId}/opened`, {}).catch(() => {});
  }, [post]);

  const pollOpen = useRef(false);
  const fetchDraft = useCallback(async (isCurrent: () => boolean = () => true) => {
    pollOpen.current = false;
    try {
      const res = await get<{ draft: EvidenceDraftSummary | null }>(`${ownerPath}/evidence-drafts/active`);
      if (!isCurrent()) return;
      setDraft(res.draft);
      setDraftError(null);
      setDraftLoaded(true);
      if (res.draft && openedDraftId.current !== res.draft.draft_id) reportOpened(res.draft.draft_id);
    } catch (err: any) {
      Sentry.captureException(err);
      if (!isCurrent()) return;
      setDraftError(apiErrorMessage(err, "Failed to load the evidence draft"));
      setDraftLoaded(true);
    }
  }, [get, ownerPath, reportOpened]);

  useEffect(() => {
    openedDraftId.current = null;
    setDispute(null);
    setBankCase(null);
    setDraft(null);
    setPreviewHtml(null);
    setPreviewStale(false);
    setPreviewError(null);
    setDisputeError(null);
    setDraftError(null);
    setDraftLoaded(false);
    setOrderTypeRequired(false);
    setOrderPickRequired(false);
    setPendingAction(null);
    setSubmission(null);
    setSubmissionError(null);
    setSubmitError(null);
  }, [ownerKey]);

  useEffect(() => {
    let current = true;
    const isCurrent = () => current;
    fetchOwner(isCurrent);
    fetchDraft(isCurrent);
    return () => { current = false; };
  }, [fetchOwner, fetchDraft]);

  const draftStatus = draft?.status;
  const inFlight = !!draftStatus && EVIDENCE_IN_PROGRESS_STATUSES.includes(draftStatus);
  const waitingMs = inFlight && draft ? dayjs.utc().diff(dayjs.utc(draft.updated_at)) : 0;
  // The server releases a run this old on the next generate, so the page stops treating it as live.
  const interrupted = inFlight && waitingMs > EVIDENCE_IN_FLIGHT_TIMEOUT_MS;
  // The run is the server's until it finishes or is released, and the page is locked for as long.
  const inProgress = inFlight && !interrupted;
  // Long past the minute the copy promises: the worker may be gone, and 15 minutes of a locked page
  // is not an answer.
  const waitOverdue = inProgress && waitingMs > EVIDENCE_STALLED_AFTER_MS;

  const trackingPending = Boolean(draft?.attention_items?.some((item) => item.code === "tracking_pending"));
  // The worker's own re-run flips the draft back in flight without a click here, so the page keeps
  // watching for it until that window has passed.
  const awaitingCarrier = draftStatus === "generated" && trackingPending
    && dayjs.utc().diff(dayjs.utc(draft?.generated_at)) < EVIDENCE_TRACKING_FOLLOW_UP_MS;

  // A tick waits for its own response rather than aborting it. Any other fetchDraft aborts the
  // poll's GET (same path) and that promise never settles, so it releases the flag on entry.
  // The poll also holds while an action is pending: a tick would abort the action's own refetch
  // the same way, and the action's `finally` would never clear the spinner.
  useEffect(() => {
    if ((!inProgress && !awaitingCarrier) || pendingAction) return;
    let current = true;
    pollOpen.current = false;
    const timer = setInterval(() => {
      if (pollOpen.current) return;
      const request = fetchDraft(() => current);
      pollOpen.current = true;
      request.finally(() => { pollOpen.current = false; });
    }, POLL_INTERVAL_MS);
    return () => { current = false; clearInterval(timer); };
  }, [inProgress, awaitingCarrier, pendingAction, fetchDraft]);

  const draftId = draft?.draft_id;
  const generatedAt = draft?.generated_at;
  // Every edit stamps changed_at server-side and leaves the PDF behind, so the live preview,
  // which is rendered fresh, reloads on it as well as on a new generation.
  const changedAt = draft?.changed_at;

  const fetchPreview = useCallback(async (id: number, isCurrent: () => boolean = () => true) => {
    try {
      const html = await getText(`/api/evidence-drafts/${id}/preview`, { slowApi: true });
      if (!isCurrent()) return;
      setPreviewHtml(html);
      setPreviewStale(false);
      setPreviewExpired(false);
      setPreviewError(null);
    } catch (err: any) {
      // 409 generateRequired means the snapshot predates this build, not that the draft is broken;
      // 409 documentExpired means its screenshots are gone, and the PDF stands in for it.
      if (err?.status === 409) {
        const expired = err?.data?.documentExpired === true;
        if (isCurrent()) { setPreviewHtml(null); setPreviewStale(!expired); setPreviewExpired(expired); setPreviewError(null); }
        return;
      }
      // Not err.message: a gateway failure's message is the raw HTML body, which is not a message.
      Sentry.captureException(err);
      if (isCurrent()) setPreviewError("Failed to load the preview");
    }
  }, [getText]);

  useEffect(() => {
    if (!draftId || !generatedAt) { setPreviewHtml(null); setPreviewStale(false); setPreviewExpired(false); setPreviewError(null); return; }
    let current = true;
    fetchPreview(draftId, () => current);
    return () => { current = false; };
  }, [draftId, generatedAt, changedAt, fetchPreview]);

  // Same triggers as the preview: the bullets come from the snapshot, the sections also from the
  // attachments, and both change exactly when the preview does.
  useEffect(() => {
    setOutlineError(false);
    if (!draftId || !generatedAt) { setOutline(null); return; }
    let current = true;
    get<EvidenceOutline>(`/api/evidence-drafts/${draftId}/outline`)
      .then((result) => {
        if (!current) return;
        // A payload without the outline's shape is a failed load, not an empty outline.
        if (Array.isArray(result?.sections)) { setOutline(result); setOutlineError(false); } else { setOutline(null); setOutlineError(true); }
      })
      .catch((err: any) => {
        // 409 generateRequired is the preview's own message; the card just has nothing to offer yet.
        if (current) setOutline(null);
        if (err?.status === 409) return;
        Sentry.captureException(err);
        if (current) setOutlineError(true);
      });
    return () => { current = false; };
  }, [draftId, generatedAt, changedAt, get]);

  const fetchSubmission = useCallback(async (id: number, isCurrent: () => boolean = () => true) => {
    try {
      const res = await get<EvidenceSubmissionView>(`/api/evidence-drafts/${id}/submission`);
      if (!isCurrent()) return;
      setSubmission(res);
      setSubmissionError(null);
    } catch (err: any) {
      // 409 generateRequired: no package to send yet, which the preview already says.
      if (err?.status === 409) {
        if (isCurrent()) { setSubmission(null); setSubmissionError(null); }
        return;
      }
      Sentry.captureException(err);
      if (!isCurrent()) return;
      // The plan on screen is the one the agent confirms: an outdated one must not stay enabled.
      setSubmission(null);
      setSubmissionError(apiErrorMessage(err, "Failed to load what goes to the processor"));
    }
  }, [get]);

  const submissionProvider = dispute && (SUBMISSION_PROVIDERS as readonly string[]).includes(dispute.pp_type) ? dispute.pp_type as SubmissionProvider : null;
  const draftPackage = draft?.package;
  // An edit stamps changed_at without touching the plan, so only the answers' own text refetches it.
  const answersKey = JSON.stringify([draft?.input_overrides?.shopifyAnswers ?? null, draft?.input_overrides?.paypalNotes ?? null, draft?.input_overrides?.adyenDefense ?? null, draft?.input_overrides?.stripeAnswers ?? null]);
  useEffect(() => {
    if (!submissionProvider || !draftId || !generatedAt || inProgress) { setSubmission(null); setSubmissionError(null); return; }
    let current = true;
    fetchSubmission(draftId, () => current);
    return () => { current = false; };
  }, [submissionProvider, draftId, generatedAt, draftPackage, answersKey, inProgress, fetchSubmission]);

  const submissionLoaded = submission !== null;
  useEffect(() => {
    let current = true;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") { hiddenSince.current = Date.now(); return; }
      const away = hiddenSince.current != null ? Date.now() - hiddenSince.current : 0;
      hiddenSince.current = null;
      if (away >= OPENED_AFTER_HIDDEN_MS && openedDraftId.current != null) reportOpened(openedDraftId.current);
      // A brand setting saved in another tab (the return address) changes what goes.
      if (submissionLoaded && draftId) fetchSubmission(draftId, () => current);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { current = false; document.removeEventListener("visibilitychange", onVisibility); };
  }, [reportOpened, submissionLoaded, draftId, fetchSubmission]);

  // A mutation outlives a navigation, so its response is discarded rather than misapplied.
  const shownOwner = useLatest(ownerKey);
  const shownOwnerGuard = () => {
    const issuedFor = ownerKey;
    return () => shownOwner.current === issuedFor;
  };

  // The ARN field saves on blur, so a generation queued right after typing has to wait for it:
  // the run snapshots the stored fields, not what is on screen. Every save in flight is tracked,
  // not just the newest, so a blur followed by a click leaves neither of them unawaited.
  const manualSaves = useRef(new Set<Promise<boolean>>());
  // Saves run one at a time: a toggle fired while an earlier PATCH is open would otherwise race it,
  // and the older response could land last and put the field back.
  const saveChain = useRef<Promise<unknown>>(Promise.resolve());
  const saveSequence = useRef(0);

  const createDraft = async (orderType?: EvidenceOrderType) => {
    const isCurrent = shownOwnerGuard();
    setPendingAction("generate");
    try {
      const res = await post<{ draft: EvidenceDraftSummary }>(
        `${ownerPath}/evidence-drafts`,
        orderType ? { orderType } : {},
        { slowApi: true },
      );
      if (!isCurrent()) return;
      setOrderTypeRequired(false);
      setOrderPickRequired(false);
      setDraft(res.draft);
    } catch (err: any) {
      if (!isCurrent()) return;
      // 409 + orderTypeRequired: the API can't tell an initial order from a renewal, so it asks.
      if (err?.status === 409 && err?.data?.orderTypeRequired) { setOrderTypeRequired(true); return; }
      // 400 + orderRequired: the API could not find the order, so the agent can name it.
      if (err?.status === 400 && err?.data?.orderRequired) setOrderPickRequired(true);
      // A 400 is the API declining the case (no order found): the toast is the whole answer.
      if (err?.status !== 400) Sentry.captureException(err);
      setToast({ message: apiErrorMessage(err, "Failed to start evidence generation"), severity: "error" });
    } finally {
      setPendingAction(null);
    }
  };

  const generate = async () => {
    if (!draft) return;
    const isCurrent = shownOwnerGuard();
    setPendingAction("generate");
    try {
      // A failed save leaves its own error toast up, and generating would snapshot stale fields.
      const saved = await Promise.all([...manualSaves.current]);
      if (saved.some((ok) => !ok)) return;
      await post(`/api/evidence-drafts/${draft.draft_id}/generate`, {});
      if (!isCurrent()) return;
      // Not awaited: another refetch of the same path (an attachment change, a failed save) can
      // abort this GET, and the superseded promise never settles, which would keep `pendingAction`
      // set and Generate disabled.
      void fetchDraft(isCurrent);
    } catch (err: any) {
      Sentry.captureException(err);
      if (!isCurrent()) return;
      setToast({ message: apiErrorMessage(err, "Failed to queue the generation"), severity: "error" });
      // A 409 means another tab already moved this draft on, so the page reloads what it missed.
      if (err?.status === 409) void fetchDraft(isCurrent);
    } finally {
      setPendingAction(null);
    }
  };

  // The agent gave up on a run that never answered. The server releases the draft, so the page is
  // workable and Generate is open again; a worker that finishes later is fenced out by the run id
  // and its result discarded. A run that answered while the agent was giving up keeps its result,
  // and the draft comes back generated.
  const stopWaiting = async () => {
    if (!draft) return;
    const isCurrent = shownOwnerGuard();
    setPendingAction("stopWaiting");
    try {
      const res = await post<{ draft: EvidenceDraftSummary }>(`/api/evidence-drafts/${draft.draft_id}/abandon`, {});
      if (!isCurrent()) return;
      setDraft(res.draft);
    } catch (err: any) {
      Sentry.captureException(err);
      if (!isCurrent()) return;
      setToast({ message: apiErrorMessage(err, "Failed to stop waiting for the generation"), severity: "error" });
    } finally {
      setPendingAction(null);
    }
  };

  const saveManualFields = (
    body: { shopifyAnswers?: Partial<Record<ShopifyAnswerField, string | null>>; paypalNotes?: Record<string, string | null>; stripeAnswers?: Partial<Record<StripeAnswerField, string | null>>; adyenDefense?: { reasonCode: string } | null; arn?: string | null; noContactAttested?: boolean; customerContact?: CustomerContact | null; productDescriptions?: Record<string, string>; excludedSections?: ExcludableSection[]; excludedFacts?: FactId[]; includedSections?: ForceableSection[]; manualRefund?: ManualRefund | null; storefrontId?: number | null; digitalDetails?: DigitalDetails; trackingNumber?: string | null },
    failureMessage: string,
  ): Promise<boolean> => {
    if (!draft) return Promise.resolve(true);
    const isCurrent = shownOwnerGuard();
    const issued = ++saveSequence.current;
    const run = async (): Promise<boolean> => {
      try {
        const res = await patch<{ draft: EvidenceDraftSummary }>(`/api/evidence-drafts/${draft.draft_id}/manual-fields`, body);
        // A save queued behind this one already holds the newer fields, so only the latest applies.
        if (isCurrent() && issued === saveSequence.current) setDraft(res.draft);
        return true;
      } catch (err: any) {
        Sentry.captureException(err);
        if (isCurrent()) setToast({ message: apiErrorMessage(err, failureMessage), severity: "error" });
        // The summary may hold an older save's result that this failure dropped; the server's copy is the truth.
        if (isCurrent()) void fetchDraft(isCurrent);
        return false;
      }
    };
    const request = saveChain.current.then(run);
    saveChain.current = request;
    manualSaves.current.add(request);
    request.finally(() => { manualSaves.current.delete(request); });
    return request;
  };

  const saveConclusion = async (paragraphs: [string, string]) => {
    if (!draft) return;
    const isCurrent = shownOwnerGuard();
    try {
      const res = await patch<{ draft: EvidenceDraftSummary }>(
        `/api/evidence-drafts/${draft.draft_id}/manual-fields`,
        { conclusion: { paragraphs } },
      );
      if (isCurrent()) setDraft(res.draft);
    } catch (err: any) {
      Sentry.captureException(err);
      // Rethrown, not toasted: a toast behind the modal is a message nobody reads.
      throw err;
    }
  };

  // Rethrown, not toasted: the dialog the edit was made in is where a refusal is read.
  const saveBankFacts = async (body: BankFactsBody): Promise<void> => {
    const isCurrent = shownOwnerGuard();
    const res = await patch<{ bankCase: BankCase }>(ownerPath, body);
    if (!isCurrent()) return;
    setBankCase(res.bankCase);
    // The edit stamped the draft's changed_at, which is what reloads the preview and the banner.
    void fetchDraft(isCurrent);
  };

  // The order the agent named is stored on the dispute; the next create builds on it. Rethrown:
  // a refusal is read in the dialog.
  const chooseOrder = async (body: EvidenceOrderBody): Promise<void> => {
    const isCurrent = shownOwnerGuard();
    try {
      await put(`${ownerPath}/evidence-order`, body, { slowApi: true });
    } catch (err: any) {
      if (err?.status !== 400 && err?.status !== 409) Sentry.captureException(err);
      throw err;
    }
    if (!isCurrent()) return;
    setOrderPickRequired(false);
    await createDraft();
  };

  // The case moves to the other order and its new draft is already queued. Rethrown like the edit:
  // a refusal is read in the dialog.
  const changeOrder = async (body: BankCaseOrderBody): Promise<void> => {
    const isCurrent = shownOwnerGuard();
    try {
      const res = await put<{ bankCase: BankCase; draft: EvidenceDraftSummary }>(`${ownerPath}/order`, body, { slowApi: true });
      if (!isCurrent()) return;
      setBankCase(res.bankCase);
      setDraft(res.draft);
    } catch (err: any) {
      // 400 another company's order, 409 a run holds the draft: the API's message is the answer.
      if (err?.status !== 400 && err?.status !== 409) Sentry.captureException(err);
      throw err;
    }
  };

  const rewriteConclusion = async () => {
    if (!draft) return;
    const isCurrent = shownOwnerGuard();
    setPendingAction("conclusion");
    try {
      const res = await post<{ conclusion: EvidenceConclusion }>(
        `/api/evidence-drafts/${draft.draft_id}/conclusion/rewrite`,
        {},
        { slowApi: true },
      );
      if (!isCurrent()) return;
      setDraft((prev) => (prev ? { ...prev, conclusion: res.conclusion } : prev));
      await fetchDraft(isCurrent);
    } catch (err: any) {
      Sentry.captureException(err);
      if (!isCurrent()) return;
      setToast({ message: apiErrorMessage(err, "Failed to write the conclusion"), severity: "error" });
    } finally {
      setPendingAction(null);
    }
  };

  // reasonCode is the Adyen reason the agent confirmed; the API refuses when the stored pick has moved past it.
  const submitEvidence = async (reasonCode?: string): Promise<boolean> => {
    if (!draft || !submissionProvider) return false;
    const isCurrent = shownOwnerGuard();
    setPendingAction("submit");
    setSubmitError(null);
    try {
      // An answer still saving would be missing from what the processor gets; a failed one shows its own toast.
      const saved = await Promise.all([...manualSaves.current]);
      if (saved.some((ok) => !ok)) return false;
      await post(`/api/evidence-drafts/${draft.draft_id}/submit`, reasonCode ? { reasonCode } : {});
      if (!isCurrent()) return false;
      setToast({ message: SUBMIT_COPY[submissionProvider].done, severity: "success" });
      await fetchDraft(isCurrent);
      return true;
    } catch (err: any) {
      // A 400 is the API refusing the submit for a reason the alert states; only a fault is reported.
      if (err?.status !== 400) Sentry.captureException(err);
      if (isCurrent()) {
        setSubmitError(apiErrorMessage(err, SUBMIT_COPY[submissionProvider].refused));
        // A refusal may still have recorded a failed send, which the summary carries.
        void fetchDraft(isCurrent);
      }
      return false;
    } finally {
      if (isCurrent()) setPendingAction(null);
    }
  };

  const onAttachmentsChange = () => { fetchDraft(shownOwnerGuard()); };

  const retryPreview = () => {
    if (draftId) fetchPreview(draftId, shownOwnerGuard());
  };

  const retryLoad = () => {
    const isCurrent = shownOwnerGuard();
    fetchOwner(isCurrent);
    fetchDraft(isCurrent);
  };

  const canDownload = Boolean(draft?.generated_at);

  const downloadPdf = async () => {
    if (!draft || !canDownload) return;
    try {
      await download(`/api/evidence-drafts/${draft.draft_id}/pdf`, { slowApi: true });
    } catch (err: any) {
      Sentry.captureException(err);
      setToast({ message: apiErrorMessage(err, "Failed to download the PDF"), severity: "error" });
    }
  };

  const downloadPackageFile = async (file: PackageFileKey) => {
    if (!draft || !canDownload) return;
    try {
      await download(`/api/evidence-drafts/${draft.draft_id}/package/${file}`, { slowApi: true });
    } catch (err: any) {
      Sentry.captureException(err);
      setToast({ message: apiErrorMessage(err, "Failed to download the split PDF"), severity: "error" });
    }
  };

  // The summary carries both stamps, so staleness is compared here rather than guessed at.
  const isStale = Boolean(draft?.generated_at && draft?.changed_at
    && dayjs(draft.changed_at).isAfter(dayjs(draft.generated_at)));

  const staleChanges = isStale && draft ? draft.changed_labels ?? [] : [];

  return {
    dispute, bankCase, draft, previewHtml, previewStale, previewExpired, previewError, outline, outlineError, loading, loadError, draftLoadFailed, draftLoaded, orderTypeRequired, orderPickRequired,
    inProgress, waitOverdue, isStale, staleChanges, trackingPending, pendingAction, canDownload,
    submission, submissionError, submitError,
    createDraft, generate, stopWaiting, saveManualFields, submitEvidence, saveConclusion, rewriteConclusion, saveBankFacts, changeOrder, chooseOrder,
    onAttachmentsChange, downloadPdf, downloadPackageFile, retryLoad, retryPreview,
    submissionProvider,
    toast, setToast,
  };
}
